/**
 * Real host portal API client — talks to /api/host/*.
 *
 * These routes are gated to the "authenticated" role by staticwebapp.config.json,
 * so the browser sends the SWA session cookie automatically and the Functions
 * host receives the injected principal. No token handling here.
 */

import type {
  DownloadPayload,
  Guest,
  HostApi,
  HostSettings,
  RemovalSummary
} from "../types";

async function requireOk(response: Response, what: string): Promise<Response> {
  if (!response.ok) {
    throw new Error(`${what} failed (${response.status})`);
  }
  return response;
}

/** Pull a filename out of a Content-Disposition header, if present. */
function filenameFromResponse(response: Response, fallback: string): string {
  const cd = response.headers.get("Content-Disposition") ?? "";
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"|"$/g, ""));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain?.[1] ?? fallback;
}

export const realHostApi: HostApi = {
  async listGuests(query = ""): Promise<Guest[]> {
    const url = query.trim()
      ? `/api/host/guests?q=${encodeURIComponent(query.trim())}`
      : "/api/host/guests";
    const response = await fetch(url, { headers: { Accept: "application/json" } });
    await requireOk(response, "Loading guests");
    return (await response.json()) as Guest[];
  },

  async exportGuest(guestId: string): Promise<DownloadPayload> {
    const response = await fetch(
      `/api/host/guests/${encodeURIComponent(guestId)}/export`,
      { headers: { Accept: "application/json" } }
    );
    await requireOk(response, "Exporting guest data");
    const blob = await response.blob();
    return { blob, filename: filenameFromResponse(response, `guest-${guestId}.json`) };
  },

  async exportConsentedCsv(): Promise<DownloadPayload> {
    const response = await fetch("/api/host/guests/export.csv", {
      headers: { Accept: "text/csv" }
    });
    await requireOk(response, "Exporting the consented list");
    const blob = await response.blob();
    return { blob, filename: filenameFromResponse(response, "guests-consented.csv") };
  },

  async deleteGuest(guestId: string): Promise<RemovalSummary> {
    const response = await fetch(`/api/host/guests/${encodeURIComponent(guestId)}`, {
      method: "DELETE"
    });
    await requireOk(response, "Deleting this guest");
    return (await response.json().catch(() => ({ guestId }))) as RemovalSummary;
  },

  async setConsent(guestId: string, granted: boolean): Promise<void> {
    const response = await fetch(
      `/api/host/guests/${encodeURIComponent(guestId)}/consent`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ granted })
      }
    );
    await requireOk(response, "Updating consent");
  },

  async getSettings(): Promise<HostSettings> {
    const response = await fetch("/api/host/settings", {
      headers: { Accept: "application/json" }
    });
    await requireOk(response, "Loading settings");
    return (await response.json()) as HostSettings;
  },

  async updateSettings(settings: HostSettings): Promise<HostSettings> {
    const response = await fetch("/api/host/settings", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings)
    });
    await requireOk(response, "Saving settings");
    return (await response.json()) as HostSettings;
  }
};
